import { Injectable, OnDestroy, computed, effect, inject, signal } from "@angular/core";
import { BlackboardStreamService } from "./blackboard-stream.service";
import type { BlackboardIndicator } from "../model/block.types";
import type {
  EvidenceBracketPresentation,
  IEvidenceBracketView,
  IEvidenceProgressEvent,
  IEvidenceRawEntry,
  IEvidenceStageState,
  IEvidenceStageView,
  IEvidenceToolBracket,
} from "../model/evidence-progress.types";

/** Appearance debounce (spec L67): the block is not rendered until 300 ms after `execute/running`. */
const APPEARANCE_DEBOUNCE_MS = 300;

/** Min-visible hold (spec L68): once shown, running holds ≥600 ms before collapsing to summary. */
const MIN_VISIBLE_MS = 600;

/**
 * Bracket-entry cap (fix round S9): unbounded-growth guard — at most 100 EPISODES live in the
 * bracket map; beyond it the OLDEST entries (creation order) are dropped first so the newest
 * episodes always survive. The raw ring keeps its own 200-entry cap, untouched by this one.
 */
const BRACKET_ENTRY_CAP = 100;

/** Raw ring cap (debug table): newest-last ring of the last 200 events, never persisted. */
const RAW_RING_LIMIT = 200;

/** Chronometer cadence (spec L53): one 1 s `setInterval` tick inside the store, not per component. */
const CLOCK_TICK_MS = 1000;

/** Container stage name: opens/closes the bracket and is never a stage row (spec L32). */
const EXECUTE_STAGE = "execute";

/** Staging stage name — tracked so the card can pre-show the staging row once it was seen. */
const STAGING_STAGE = "staging";

/** Null fallback key segment for events whose `task_id` is null (store contract). */
const NULL_TASK_KEY = "—";

/** Status that opens brackets and stage rows; unknown future statuses are stored verbatim. */
const RUNNING_STATUS = "running";

/** Status that closes brackets and captures size/duration; unknown future statuses are stored verbatim. */
const COMPLETED_STATUS = "completed";

/**
 * Identity (base) key of an episode: `task_id ?? "—"` + `#rowIndex` when a row index is present
 * (store contract — the exact scheme is store-owned as long as batch rows stay separable). Batch
 * rows therefore map to distinct base keys (`task#0`, `task#1`, …) and never merge across rows.
 */
const bracketKey = (taskId: string | null, rowIndex: number | null): string =>
  rowIndex == null ? taskId ?? NULL_TASK_KEY : `${taskId ?? NULL_TASK_KEY}#${rowIndex}`;

/**
 * Pure presentation state machine (spec L66-70 timing clauses). `shownAt` is derived from
 * `openedAt` (event time), so the presentation is a pure function of (bracket, finalized, now) —
 * deterministic under fake timers. The fast-call clause (<250 ms running→completed, spec L69) is
 * delivered by the 300 ms appearance debounce, which subsumes it: a bracket closed before
 * `shownAt` never presented as running and collapses straight to summary.
 */
const bracketPresentation = (
  bracket: Readonly<IEvidenceToolBracket>,
  finalized: boolean,
  now: number,
): EvidenceBracketPresentation => {
  if (finalized) return "summary"; // plan_close: everything collapses to summary immediately
  const shownAt = bracket.openedAt + APPEARANCE_DEBOUNCE_MS;
  if (!bracket.closed) return now >= shownAt ? "running" : "hidden";
  if (now < shownAt) return "hidden"; // appearance debounce still pending — not rendered at all
  if (bracket.closedAt != null && bracket.closedAt < shownAt) return "summary"; // fast path: never shown running
  return now >= shownAt + MIN_VISIBLE_MS ? "summary" : "running"; // min-visible hold after a real show
};

/**
 * Best-known total size for a bracket: the `execute/completed` capture when present (spec L58 —
 * the execute size is THE size), else the sum of completed inner-stage sizes; null until any
 * size is known.
 */
const totalSizeBytesOf = (stages: ReadonlyArray<IEvidenceStageState>): number | null => {
  const execute = stages.find((stage) => stage.stage === EXECUTE_STAGE);
  if (execute && execute.status === COMPLETED_STATUS && execute.sizeBytes != null) return execute.sizeBytes;
  let sum = 0;
  let sawSize = false;
  for (const stage of stages) {
    if (stage.stage !== EXECUTE_STAGE && stage.status === COMPLETED_STATUS && stage.sizeBytes != null) {
      sum += stage.sizeBytes;
      sawSize = true;
    }
  }
  return sawSize ? sum : null;
};

/**
 * Best-known produced data kind for an episode: the `execute` container's kind (the backend's
 * summary kind, captured on `execute/completed`) when non-null, else the first inner stage that
 * captured one (staging carries the REAL DataKind — visible while the episode still runs); null
 * until any kind is known.
 */
const bracketKindOf = (stages: ReadonlyArray<IEvidenceStageState>): string | null => {
  const execute = stages.find((stage) => stage.stage === EXECUTE_STAGE);
  if (execute?.kind != null) return execute.kind;
  const inner = stages.find((stage) => stage.stage !== EXECUTE_STAGE && stage.kind != null);
  return inner?.kind ?? null;
};

/**
 * Reduces the ephemeral `evidence_progress` AG-UI Custom events (vexcel tool-execution
 * traceability) into per-call EPISODES for the debug-only evidence card family
 * (evidence-progress-spec.md, v1.1 S6). One episode per tool-call execution — opened by
 * `execute/running`, fed by the inner `api_call`/`staging` events, closed by
 * `execute/completed` — with batch rows kept separable by `row_index`. Re-executing the same
 * task+row STACKS a new episode (identity key + `~occurrence`); the previous one is preserved
 * verbatim, and intermediate events always update the most recent OPEN episode of their key in
 * place. The card consumes the derived `view` (stacked, creation order); the debug dialog
 * consumes `rawEvents`.
 *
 * Provided per chat-container (NOT root) beside `BlackboardStreamService`, same lifecycle: the
 * terminal signal comes from injecting `BlackboardStreamService` and reacting ONLY to the
 * `plan_run` phase — `plan_close` completed finalizes (open episodes close, clock stops) and
 * `plan_prepare` is absorbed as an explicit no-op (it must NEVER close anything — the
 * discriminant is always `step`). The finalize is terminal, NOT a wipe: the store PERSISTS
 * afterwards (fix round S9) until an explicit `clear()` or container teardown, and episodes
 * from later runs keep stacking on top (occurrence numbering continues across finalize rounds).
 *
 * Shape-guarded by contract: `store()` never throws — malformed payloads are logged with
 * `console.error` and ignored, malformed KEYS are nulled per key (the valid part still reduces),
 * unknown stage/status strings are tolerated verbatim and orphaned inner events reduce to
 * nothing (the raw ring still records every valid event). Events live in memory only: nothing is
 * persisted or replayed beyond this store.
 */
@Injectable()
export class EvidenceProgressStore implements OnDestroy {
  /** Terminal-signal source (same container): the `plan_run` phase indicator. */
  readonly #blackboard = inject(BlackboardStreamService);

  readonly #brackets = signal<ReadonlyMap<string, Readonly<IEvidenceToolBracket>>>(new Map());

  /**
   * Episode occurrence counters per identity (base) key: how many episodes each base has created
   * (0-based → the next episode's occurrence). Episode keys derive from it: occurrence 0 keeps
   * the plain base key, N ≥ 1 appends `~N`. Plain bookkeeping — the `#brackets` signal stays the
   * reactive source of truth.
   */
  readonly #episodeCounts = new Map<string, number>();

  /**
   * Currently OPEN episode key per identity (base) key. Episodes stack per base, and at most one
   * episode per base can be open at a time (a stacked `execute/running` freezes the open
   * predecessor, `execute/completed` and the finalize close the open one) — this index resolves
   * intermediate events and completed events to the most recent OPEN episode of their key.
   */
  readonly #openByBase = new Map<string, string>();

  readonly #rawEvents = signal<ReadonlyArray<Readonly<IEvidenceRawEntry>>>([]);

  readonly #stagingSeen = signal(false);

  readonly #finalized = signal(false);

  /** Client clock driving the chronometer and the presentation timing rules. */
  readonly #now = signal(Date.now());

  #clock: ReturnType<typeof setInterval> | null = null;

  #transitionTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Last `plan_run` indicator ENTRY seen by the terminal effect. The blackboard store replaces
   * the entry object on every `plan_run` event, so identity is the per-event level trigger: the
   * effect can be flushed repeatedly with an UNCHANGED indicator (scheduler flushes), and only a
   * genuinely new `plan_run` event may finalize.
   */
  #lastPlanRunIndicator: BlackboardIndicator | null = null;

  /** Live tool episodes, keyed by EPISODE key (identity key + `~occurrence` from the first re-execution on; batch rows separable). Stacked episodes are separate entries — the map is append-only in creation order, capped at `BRACKET_ENTRY_CAP` (oldest dropped). Reactive. */
  readonly brackets = this.#brackets.asReadonly();

  /**
   * Raw event ring for the debug table's "Evidence events" section — the last `RAW_RING_LIMIT`
   * events, NEWEST LAST. Never persisted. Reactive.
   */
  readonly rawEvents = this.#rawEvents.asReadonly();

  /** True while at least one episode is open (spec liveness). Reactive. */
  readonly isLive = computed(() => {
    for (const bracket of this.#brackets().values()) {
      if (!bracket.closed) return true;
    }
    return false;
  });

  /** True once any staging event was seen — lets the card pre-show the staging row (spec L45). Reactive. */
  readonly stagingSeen = this.#stagingSeen.asReadonly();

  /**
   * Derived per-episode presentation view for the evidence card: `hidden` entries are excluded
   * entirely (nothing is rendered inside the appearance debounce), running episodes carry the
   * live chronometer, closed ones collapse to summary after the min-visible hold. The array is
   * ordered by EPISODE CREATION, oldest first, globally (append-only map insertion order): the
   * card stacks a group's episodes chronologically by grouping on `groupKey` in array order —
   * `occurrence` (0 = first execution) is the per-group chronological index and episodes of one
   * group are not guaranteed contiguous in the array (v1.1 S6/S7). Recomputed on every store
   * mutation, 1 s clock tick and scheduled transition. Reactive.
   */
  readonly view = computed<ReadonlyArray<IEvidenceBracketView>>(() => {
    const now = this.#now();
    const finalized = this.#finalized();
    const result: IEvidenceBracketView[] = [];
    for (const [key, bracket] of this.#brackets()) {
      const presentation = bracketPresentation(bracket, finalized, now);
      if (presentation === "hidden") continue;
      const innerStages = bracket.stages.filter((stage) => stage.stage !== EXECUTE_STAGE);
      const execute = bracket.stages.find((stage) => stage.stage === EXECUTE_STAGE) ?? null;
      let stageDurationSum = 0;
      let sawStageDuration = false;
      for (const stage of innerStages) {
        if (stage.status === COMPLETED_STATUS && stage.durationMs != null) {
          stageDurationSum += stage.durationMs;
          sawStageDuration = true;
        }
      }
      const elapsedEnd = bracket.closed && bracket.closedAt != null ? bracket.closedAt : now;
      const runningStage = innerStages.find((stage) => stage.status === RUNNING_STATUS) ?? null;
      let runningStageMs: number | null = null;
      if (runningStage?.startedAtMs != null) {
        runningStageMs = Math.max(Math.min(now, elapsedEnd) - runningStage.startedAtMs, 0);
      }
      let summaryDurationMs: number | null = null;
      if (execute && execute.status === COMPLETED_STATUS && execute.durationMs != null) {
        summaryDurationMs = execute.durationMs;
      } else if (sawStageDuration) {
        summaryDurationMs = stageDurationSum;
      }
      result.push({
        key,
        bracket,
        presentation,
        elapsedMs: Math.max(elapsedEnd - bracket.openedAt, 0),
        stages: innerStages.map(
          (stage): IEvidenceStageView => ({
            stage: stage.stage,
            status: stage.status,
            sizeBytes: stage.sizeBytes,
            durationMs: stage.durationMs,
            detail: stage.detail,
            kind: stage.kind ?? null,
            layer: stage.layer ?? null,
          }),
        ),
        summarySizeBytes: bracket.totalSizeBytes,
        summaryDurationMs,
        runningStageMs,
        groupKey: bracketKey(bracket.taskId, bracket.rowIndex),
        occurrence: bracket.occurrence ?? 0,
        label: bracket.label ?? null,
        kind: bracket.kind ?? null,
      });
    }
    return result;
  });

  constructor() {
    // Terminal signal (spec L72-76): react ONLY to the `plan_run` phase — the discriminant is
    // always `step`. `plan_close` completed finalizes; `plan_prepare` is absorbed as an explicit
    // no-op (it enables nothing here and must NEVER close anything); every other phase/step
    // combination is ignored. Level-triggered per EVENT: the effect may be flushed repeatedly
    // with an unchanged indicator, so only a new `plan_run` entry object can reach finalize.
    effect(() => {
      const indicator = this.#blackboard.indicators().plan_run;
      if (!indicator || indicator === this.#lastPlanRunIndicator) return;
      this.#lastPlanRunIndicator = indicator;
      if (indicator.step === "plan_close" && indicator.status === COMPLETED_STATUS) this.#finalize();
    });
  }

  /**
   * Reduce one `evidence_progress` Custom event. Accepts the full AG-UI envelope
   * (`{type, name, value}`) or a bare payload (legacy 9-key or v1.1 12-key); every other shape is
   * logged with `console.error` and ignored. Malformed keys are nulled per key (the valid part
   * still reduces and the event still reaches the raw ring) and keys ABSENT from a legacy
   * payload normalize to null without any report; a payload without a usable `stage`/`status`
   * is dropped whole. Unknown future stage/status strings are tolerated verbatim. Never throws
   * from the stream path.
   */
  store(event: unknown): void {
    this.#now.set(Date.now());
    const value = this.#extractValue(event);
    if (!value) return;
    const normalized = this.#normalize(value);
    if (!normalized) return;
    this.#pushRaw({ at: Date.now(), event: normalized });
    this.#reduce(normalized);
    this.#scheduleNextTransition();
    this.#syncClock();
  }

  /**
   * Empties every signal and cancels the transition timer and clock interval. The store is NOT
   * auto-cleared at the terminal (S9 persistence): this explicit call is the emptier — agent
   * switch or container teardown (same lifecycle as `BlackboardStreamService`).
   */
  clear(): void {
    if (this.#transitionTimer != null) {
      clearTimeout(this.#transitionTimer);
      this.#transitionTimer = null;
    }
    this.#brackets.set(new Map());
    this.#rawEvents.set([]);
    this.#stagingSeen.set(false);
    this.#finalized.set(false);
    this.#episodeCounts.clear();
    this.#openByBase.clear();
    this.#now.set(Date.now());
    this.#syncClock();
  }

  /** Stops all timers — per-container teardown (the store is not root-scoped). */
  ngOnDestroy(): void {
    if (this.#clock != null) {
      clearInterval(this.#clock);
      this.#clock = null;
    }
    if (this.#transitionTimer != null) {
      clearTimeout(this.#transitionTimer);
      this.#transitionTimer = null;
    }
  }

  /** Envelope extraction: full AG-UI Custom envelope or bare payload (legacy 9-key or v1.1 12-key); else logged + null. */
  #extractValue(event: unknown): Record<string, unknown> | null {
    if (event == null || typeof event !== "object" || Array.isArray(event)) {
      console.error("evidence_progress: unexpected payload shape", event);
      return null;
    }
    const envelope = event as Record<string, unknown>;
    const name = envelope.name;
    if (typeof name === "string" && name !== "evidence_progress") {
      console.error("evidence_progress: unexpected event name", name);
      return null;
    }
    const value = envelope.value;
    if (value != null && typeof value === "object" && !Array.isArray(value)) {
      return value as Record<string, unknown>;
    }
    // No usable envelope `value` (absent or null): the event may be a bare 9-key payload instead.
    return envelope;
  }

  /** Per-key normalization: malformed keys are nulled and reported once; stage/status are mandatory. */
  #normalize(value: Record<string, unknown>): IEvidenceProgressEvent | null {
    const invalid: string[] = [];
    const stage = typeof value.stage === "string" && value.stage.length > 0 ? value.stage : null;
    const status = typeof value.status === "string" && value.status.length > 0 ? value.status : null;
    if (stage == null) invalid.push("stage");
    if (status == null) invalid.push("status");
    const toolId = this.#optionalString(value.tool_id, "tool_id", invalid);
    const taskId = this.#optionalString(value.task_id, "task_id", invalid);
    const resolverId = this.#optionalString(value.resolver_id, "resolver_id", invalid);
    const detail = this.#optionalString(value.detail, "detail", invalid);
    const label = this.#optionalString(value.label, "label", invalid);
    const kind = this.#optionalString(value.kind, "kind", invalid);
    const layer = this.#optionalString(value.layer, "layer", invalid);
    const sizeBytes = this.#optionalSize(value.size_bytes, "size_bytes", invalid);
    const durationMs = this.#optionalSize(value.duration_ms, "duration_ms", invalid);
    const rowIndex = this.#optionalRowIndex(value.row_index, "row_index", invalid);
    if (invalid.length > 0) {
      console.error("evidence_progress: malformed keys ignored:", invalid.join(","), value);
    }
    if (stage == null || status == null) return null; // unusable without a stage/status
    return {
      stage,
      status,
      tool_id: toolId,
      task_id: taskId,
      resolver_id: resolverId,
      size_bytes: sizeBytes,
      duration_ms: durationMs,
      detail,
      row_index: rowIndex,
      label,
      kind,
      layer,
    };
  }

  /** String key tolerance: non-empty strings pass; wrong types are logged; empty strings null quietly. */
  #optionalString(raw: unknown, key: string, invalid: string[]): string | null {
    if (typeof raw === "string") return raw.length > 0 ? raw : null;
    if (raw != null) invalid.push(key);
    return null;
  }

  /** Size/duration tolerance: finite non-negative numbers pass; everything else is logged and nulled. */
  #optionalSize(raw: unknown, key: string, invalid: string[]): number | null {
    if (typeof raw === "number" && Number.isFinite(raw)) {
      if (raw >= 0) return raw;
      invalid.push(key);
      return null;
    }
    if (raw != null) invalid.push(key);
    return null;
  }

  /** Row-index tolerance: integers pass; everything else is logged and nulled. */
  #optionalRowIndex(raw: unknown, key: string, invalid: string[]): number | null {
    if (typeof raw === "number" && Number.isInteger(raw)) return raw;
    if (raw != null) invalid.push(key);
    return null;
  }

  /** Raw ring push, capped at `RAW_RING_LIMIT` entries, NEWEST LAST (oldest dropped first). */
  #pushRaw(entry: IEvidenceRawEntry): void {
    this.#rawEvents.update((events) => {
      if (events.length < RAW_RING_LIMIT) return [...events, entry];
      return [...events.slice(events.length - RAW_RING_LIMIT + 1), entry];
    });
  }

  /** Bracket reduction: the container stage opens/closes; inner stages attach to a target bracket. */
  #reduce(event: IEvidenceProgressEvent): void {
    if (event.stage === STAGING_STAGE) this.#stagingSeen.set(true);
    if (event.stage === EXECUTE_STAGE) {
      if (event.status === RUNNING_STATUS) {
        this.#openBracket(event);
      } else if (event.status === COMPLETED_STATUS) {
        this.#closeBracket(event);
      } else {
        // `error` or unknown future status on the container: record verbatim, never closes
        // (the plan terminal finalize is the backstop that closes everything).
        const key = this.#resolveTargetKey(event);
        if (key != null) this.#upsertStage(key, event);
      }
      return;
    }
    // Inner stage (`api_call`/`staging`/unknown future): attach to the most recent OPEN episode
    // of the event's identity key. Orphaned inner events (nothing open pairs with them) reduce
    // to nothing — best-effort.
    const key = this.#resolveTargetKey(event);
    if (key == null) return;
    this.#upsertStage(key, event);
  }

  /**
   * Opens a NEW episode on `execute/running` (v1.1 S6): a running event on an identity key that
   * already has an episode — open OR closed — stacks a fresh one; the open predecessor is frozen
   * verbatim first (closed at this instant; its stages and summary never mutate again, and the
   * standard min-visible presentation rule collapses it to summary). The occurrence counter
   * names the episode: occurrence 0 keeps the plain base key, N ≥ 1 appends `~N`. Episode keys
   * are unique per base occurrence; a task id that itself ends in `~<int>` could collide with
   * another base's suffixed episode key — the same accepted engine-controlled-id assumption as
   * the base scheme's `#rowIndex` segment. The new episode APPENDS to the map (insertion order =
   * creation order, oldest first — the view's stacking order); closes never reorder.
   */
  #openBracket(event: IEvidenceProgressEvent): void {
    const base = bracketKey(event.task_id, event.row_index);
    const now = Date.now();
    const openKey = this.#openByBase.get(base) ?? null;
    const occurrence = this.#episodeCounts.get(base) ?? 0;
    this.#episodeCounts.set(base, occurrence + 1);
    const key = occurrence === 0 ? base : `${base}~${occurrence}`;
    const bracket: IEvidenceToolBracket = {
      taskId: event.task_id,
      toolId: event.tool_id,
      resolverId: event.resolver_id,
      rowIndex: event.row_index,
      openedAt: now,
      closedAt: null,
      stages: [
        {
          stage: EXECUTE_STAGE,
          status: RUNNING_STATUS,
          sizeBytes: null,
          durationMs: null,
          detail: null,
          startedAtMs: now,
          kind: event.kind ?? null,
          layer: event.layer ?? null,
        },
      ],
      closed: false,
      totalSizeBytes: null,
      label: event.label ?? null,
      kind: event.kind ?? null,
      occurrence,
    };
    this.#brackets.update((brackets) => {
      const next = new Map(brackets);
      const open = openKey != null ? brackets.get(openKey) : null;
      if (open && !open.closed) {
        // Freeze the open predecessor verbatim before the new episode lands (S6 stacking rule).
        next.set(openKey, { ...open, closed: true, closedAt: now });
      }
      next.set(key, bracket); // append-only: new episode keys are unique per base occurrence
      return next;
    });
    this.#openByBase.set(base, key);
    this.#enforceBracketCap();
    // An episode opening after a finalize is a NEW live activity: re-arm the lifecycle so a
    // later plan_close can finalize it again (S9: the finalize never wiped the store, so the
    // new episode stacks on the persisted older ones).
    if (this.#finalized()) this.#finalized.set(false);
  }

  /**
   * Unbounded-growth guard (S9): keeps at most `BRACKET_ENTRY_CAP` bracket entries, dropping the
   * OLDEST first (append-only creation order = map insertion order) so the newest episodes are
   * always kept. Evicting an OPEN episode also drops its `#openByBase` entry — the stale open
   * pointer must not resolve later events onto an evicted (missing) episode. `#episodeCounts`
   * is bookkeeping-only (base → next occurrence) and stays intact on purpose: occurrence
   * numbering keeps continuing after eviction or a finalize (a per-finalize reset would collide
   * with the persisted episode keys and overwrite them).
   */
  #enforceBracketCap(): void {
    const brackets = this.#brackets();
    const overflow = brackets.size - BRACKET_ENTRY_CAP;
    if (overflow <= 0) return;
    const evicted = new Set<string>();
    for (const key of brackets.keys()) {
      if (evicted.size >= overflow) break;
      evicted.add(key);
    }
    this.#brackets.update((current) => {
      const next = new Map(current);
      for (const key of evicted) next.delete(key);
      return next;
    });
    for (const [base, openKey] of this.#openByBase) {
      if (evicted.has(openKey)) this.#openByBase.delete(base);
    }
  }

  /** Closes the event's episode on `execute/completed`, capturing size/duration on the container. */
  #closeBracket(event: IEvidenceProgressEvent): void {
    const key = this.#resolveTargetKey(event);
    if (key == null) return; // nothing open pairs with the event — tolerated (ephemeral stream)
    const target = this.#brackets().get(key) ?? null;
    this.#upsertStage(key, event); // execute/completed size/duration land on the container stage
    const now = Date.now();
    this.#brackets.update((brackets) => {
      const bracket = brackets.get(key);
      if (!bracket || bracket.closed) return brackets;
      const next = new Map(brackets);
      next.set(key, { ...bracket, closed: true, closedAt: now });
      return next;
    });
    if (target) this.#openByBase.delete(bracketKey(target.taskId, target.rowIndex));
  }

  /**
   * Target resolution shared by attach and close (v1.1 S6, round-1 semantics preserved):
   * identity-bearing events resolve to the most recent OPEN episode of their own identity key
   * (pairing `row_index`/`task_id`); an identity whose episodes are all CLOSED is never reused
   * (a late duplicate must not close or pollute someone else's episode); an identity never seen
   * — and identity-less events — fall back tolerantly to the most recently CREATED open episode
   * (append-only insertion order); null when nothing is open.
   */
  #resolveTargetKey(event: IEvidenceProgressEvent): string | null {
    const base = bracketKey(event.task_id, event.row_index);
    if (event.task_id != null || event.row_index != null) {
      const openKey = this.#openByBase.get(base) ?? null;
      if (openKey != null) return openKey;
      if (this.#episodeCounts.has(base)) return null;
    }
    let latest: string | null = null;
    for (const [key, bracket] of this.#brackets()) {
      if (!bracket.closed) latest = key;
    }
    return latest;
  }

  /**
   * Upserts the event's stage state on the episode (matched by stage name, event order kept):
   * `running` opens/refreshes the row (size/duration stay null — magnitude rule, spec L54) and
   * keeps the original `startedAtMs` on repeats; `completed` captures size/duration; `error` and
   * unknown future statuses are stored verbatim. Per stage, `kind`/`layer` keep the latest
   * non-null capture (null-coalesced to the previous value); the episode's `label` takes the
   * latest non-null label seen on any of its events, and `totalSizeBytes`/`kind` are recomputed
   * per mutation.
   */
  #upsertStage(key: string, event: IEvidenceProgressEvent): void {
    this.#brackets.update((brackets) => {
      const bracket = brackets.get(key);
      if (!bracket) return brackets;
      const previous = bracket.stages.find((stage) => stage.stage === event.stage) ?? null;
      if (previous && previous.status === RUNNING_STATUS && event.status === RUNNING_STATUS) {
        return brackets; // idempotent repeat running event
      }
      const running = event.status === RUNNING_STATUS;
      let startedAtMs: number | null = null;
      if (running) {
        startedAtMs = previous && previous.status === RUNNING_STATUS ? previous.startedAtMs : Date.now();
      } else if (previous) {
        startedAtMs = previous.startedAtMs;
      }
      const stage: IEvidenceStageState = {
        stage: event.stage,
        status: event.status,
        sizeBytes: running ? null : event.size_bytes ?? previous?.sizeBytes ?? null,
        durationMs: running ? null : event.duration_ms ?? previous?.durationMs ?? null,
        detail: event.detail ?? previous?.detail ?? null,
        startedAtMs,
        kind: event.kind ?? previous?.kind ?? null,
        layer: event.layer ?? previous?.layer ?? null,
      };
      const stages = previous
        ? bracket.stages.map((existing) => (existing.stage === event.stage ? stage : existing))
        : [...bracket.stages, stage];
      const next = new Map(brackets);
      next.set(key, {
        ...bracket,
        stages,
        totalSizeBytes: totalSizeBytesOf(stages),
        kind: bracketKindOf(stages),
        label: event.label ?? bracket.label ?? null,
      });
      return next;
    });
  }

  /**
   * Terminal finalize (`plan_close` completed): collapse open episodes to summary, stop the
   * clock, isLive false — and PERSIST (S9): no scheduled wipe; `clear()` remains explicit.
   */
  #finalize(): void {
    if (this.#finalized()) return; // already terminal — a stale indicator never re-arms
    const now = Date.now();
    this.#now.set(now);
    this.#brackets.update((brackets) => {
      let changed = false;
      const next = new Map<string, IEvidenceToolBracket>();
      for (const [key, bracket] of brackets) {
        if (bracket.closed) {
          next.set(key, bracket);
          continue;
        }
        changed = true;
        next.set(key, { ...bracket, closed: true, closedAt: now });
      }
      return changed ? next : brackets;
    });
    this.#openByBase.clear(); // every episode closed — no open target may resolve afterwards
    this.#finalized.set(true);
    this.#syncClock(); // terminal: the clock stops (spec L75)
  }

  /**
   * Schedules the next presentation transition (appearance reveal or min-visible collapse) as a
   * single one-shot timer at the nearest pending deadline, keeping the timing clauses exact
   * (sub-second) while the 1 s interval drives only the chronometer.
   */
  #scheduleNextTransition(): void {
    if (this.#transitionTimer != null) {
      clearTimeout(this.#transitionTimer);
      this.#transitionTimer = null;
    }
    if (this.#finalized()) return; // terminal: presentation is frozen to summary
    const now = Date.now();
    let deadline: number | null = null;
    for (const bracket of this.#brackets().values()) {
      const shownAt = bracket.openedAt + APPEARANCE_DEBOUNCE_MS;
      if (now < shownAt) {
        // Appearance debounce pending (open, or closed inside the debounce): the reveal — or the
        // fast-path summary — lands at shownAt.
        deadline = deadline == null ? shownAt : Math.min(deadline, shownAt);
        continue;
      }
      if (bracket.closed) {
        if (bracket.closedAt != null && bracket.closedAt < shownAt) continue; // fast path: already summary
        const collapseAt = shownAt + MIN_VISIBLE_MS;
        if (now < collapseAt) deadline = deadline == null ? collapseAt : Math.min(deadline, collapseAt);
      }
      // Open and past the debounce: running with no pending transition (its close schedules the next).
    }
    if (deadline != null) {
      this.#transitionTimer = setTimeout(
        () => {
          this.#transitionTimer = null;
          this.#now.set(Date.now());
          this.#scheduleNextTransition();
        },
        Math.max(deadline - now, 0),
      );
    }
  }

  /** Starts the 1 s clock while any bracket is open; stops it the moment none is. */
  #syncClock(): void {
    let open = false;
    for (const bracket of this.#brackets().values()) {
      if (!bracket.closed) {
        open = true;
        break;
      }
    }
    if (open && this.#clock == null) {
      this.#clock = setInterval(() => this.#now.set(Date.now()), CLOCK_TICK_MS);
    } else if (!open && this.#clock != null) {
      clearInterval(this.#clock);
      this.#clock = null;
    }
  }
}
